import type {
  Model,
  ModelRequest,
  ModelSettings,
  ModelSettingsReasoning,
} from '../model';
import type { AgentInputItem } from '../types';
import { Usage } from '../usage';
import {
  buildCompactSummaryPrompt,
  buildCompactSummarySystemInstructions,
  extractCompactSummaryText,
} from './prompt';

export type CompactSummaryModelResult = {
  summary: string;
  usage: Usage;
};

export async function summarizeForCompaction(options: {
  model: Model;
  items: AgentInputItem[];
  instructions?: string;
  modelSettings?: ModelSettings;
  signal?: AbortSignal;
}): Promise<CompactSummaryModelResult> {
  const { reasoning, ...modelSettings } = options.modelSettings ?? {};

  const request: ModelRequest = {
    systemInstructions: buildCompactSummarySystemInstructions({
      customInstructions: options.instructions,
    }),
    input: [
      {
        type: 'message',
        role: 'user',
        content: buildCompactSummaryPrompt({
          items: options.items,
        }),
      },
    ],
    modelSettings: {
      toolChoice: 'none',
      reasoning: withDefaultNoReasoning(reasoning),
      ...modelSettings,
    },
    tools: [],
    toolsExplicitlyProvided: true,
    handoffs: [],
    outputType: 'text',
    tracing: 'enabled_without_data',
    signal: options.signal,
  };

  const response = await options.model.getResponse(request);

  const text = response.output
    .flatMap((item) => {
      if (!item || typeof item !== 'object' || item.type !== 'message') {
        return [];
      }
      if (typeof item.content === 'string') {
        return [item.content];
      }
      return item.content.flatMap((part) => {
        if (!part || typeof part !== 'object') {
          return [];
        }
        return part.type === 'output_text' ? [part.text] : [];
      });
    })
    .join('');

  const summary = extractCompactSummaryText(text);
  if (!summary) {
    throw new Error('Compaction summarizer returned no text output.');
  }

  return {
    summary,
    usage: response.usage ?? new Usage(),
  };
}

function withDefaultNoReasoning(
  reasoning: ModelSettingsReasoning | undefined,
): ModelSettingsReasoning {
  return {
    effort: 'none',
    ...(reasoning ?? {}),
  };
}
